import React, { useState, useEffect } from 'react';
import { Search, Plus, Eye, MessageCircle, Trash2, X, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { apiClient } from '../../utils/apiHelper';

const ComplinList = () => {
  const [complaints, setComplaints] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [resolution, setResolution] = useState('');
  const [formData, setFormData] = useState({
    complaint_date: new Date().toISOString().split('T')[0],
    complainant_name: '',
    complainant_type: 'Student',
    phone: '',
    email: '',
    complaint_type: 'Academic',
    subject: '',
    description: '',
    priority: 'Medium',
    status: 'Open',
    assigned_to: ''
  });

  useEffect(() => { fetchComplaints(); }, []);

  const fetchComplaints = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/api/frontdesk/complaints');
      setComplaints(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching complaints:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/api/frontdesk/complaints', formData);
      alert('Complaint added successfully!');
      setShowModal(false);
      resetForm();
      fetchComplaints();
    } catch (error) {
      alert('Failed to add complaint');
    }
  };

  const handleResolve = async () => {
    if (!resolution.trim()) {
      alert('Please enter resolution details');
      return;
    }
    try {
      await apiClient.put(`/api/frontdesk/complaints/${selectedComplaint.id}/resolve?resolution=${encodeURIComponent(resolution)}`);
      alert('Complaint resolved successfully!');
      setShowResolveModal(false);
      setResolution('');
      setSelectedComplaint(null);
      fetchComplaints();
    } catch (error) {
      alert('Failed to resolve complaint');
    }
  };

  const handleDelete = async (complaintId) => {
    if (!window.confirm('Are you sure you want to delete this complaint?')) return;
    try {
      await apiClient.delete(`/api/frontdesk/complaints/${complaintId}`);
      fetchComplaints();
    } catch (error) {
      alert('Failed to delete complaint');
    }
  };

  const resetForm = () => {
    setFormData({
      complaint_date: new Date().toISOString().split('T')[0],
      complainant_name: '',
      complainant_type: 'Student',
      phone: '',
      email: '',
      complaint_type: 'Academic',
      subject: '',
      description: '',
      priority: 'Medium',
      status: 'Open',
      assigned_to: ''
    });
  };

  const filteredComplaints = complaints.filter(c =>
    c.complainant_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.complaint_number?.includes(searchTerm)
  );

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Complaint List</CardTitle>
          <Button onClick={() => setShowModal(true)}>
            <Plus className="h-4 w-4 mr-2" /> Add Complaint
          </Button>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <Input
                type="text"
                placeholder="Search by name, subject, or complaint ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Complaint #</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Complainant</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Subject</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Priority</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredComplaints.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center p-8 text-gray-500">
                      {loading ? 'Loading...' : 'No complaints found. Click "Add Complaint" to create a new entry.'}
                    </td>
                  </tr>
                ) : (
                  filteredComplaints.map((complaint) => (
                    <tr key={complaint.id} className="hover:bg-gray-50">
                      <td className="p-3 text-sm font-medium">{complaint.complaint_number || '-'}</td>
                      <td className="p-3 text-sm">{complaint.complaint_date}</td>
                      <td className="p-3 text-sm">{complaint.complainant_name}</td>
                      <td className="p-3 text-sm">{complaint.complaint_type}</td>
                      <td className="p-3 text-sm">{complaint.subject}</td>
                      <td className="p-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          complaint.priority === 'High' ? 'bg-red-100 text-red-800' :
                          complaint.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {complaint.priority}
                        </span>
                      </td>
                      <td className="p-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          complaint.status === 'Resolved' ? 'bg-green-100 text-green-800' :
                          complaint.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {complaint.status}
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="flex gap-2">
                          {complaint.status !== 'Resolved' && (
                            <button 
                              onClick={() => { setSelectedComplaint(complaint); setShowResolveModal(true); }} 
                              className="text-green-600 hover:text-green-800"
                              title="Resolve"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>
                          )}
                          <button onClick={() => handleDelete(complaint.id)} className="text-red-600 hover:text-red-800">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Add Complaint Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Add New Complaint</h2>
              <button onClick={() => setShowModal(false)}><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Complaint Date *</Label>
                  <Input type="date" required value={formData.complaint_date} onChange={(e) => setFormData({...formData, complaint_date: e.target.value})} />
                </div>
                <div>
                  <Label>Complainant Type *</Label>
                  <Select value={formData.complainant_type} onValueChange={(v) => setFormData({...formData, complainant_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Student">Student</SelectItem>
                      <SelectItem value="Parent">Parent</SelectItem>
                      <SelectItem value="Staff">Staff</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Complainant Name *</Label>
                  <Input required value={formData.complainant_name} onChange={(e) => setFormData({...formData, complainant_name: e.target.value})} />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Complaint Type *</Label>
                  <Select value={formData.complaint_type} onValueChange={(v) => setFormData({...formData, complaint_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Academic">Academic</SelectItem>
                      <SelectItem value="Infrastructure">Infrastructure</SelectItem>
                      <SelectItem value="Staff">Staff</SelectItem>
                      <SelectItem value="Hostel">Hostel</SelectItem>
                      <SelectItem value="Transport">Transport</SelectItem>
                      <SelectItem value="Fees">Fees</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Priority *</Label>
                  <Select value={formData.priority} onValueChange={(v) => setFormData({...formData, priority: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Low">Low</SelectItem>
                      <SelectItem value="Medium">Medium</SelectItem>
                      <SelectItem value="High">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Subject *</Label>
                <Input required value={formData.subject} onChange={(e) => setFormData({...formData, subject: e.target.value})} />
              </div>
              <div>
                <Label>Description *</Label>
                <textarea 
                  required 
                  className="w-full p-2 border rounded-lg" 
                  rows="3"
                  value={formData.description} 
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                />
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
                <Button type="submit">Add Complaint</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Resolve Complaint Modal */}
      {showResolveModal && selectedComplaint && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Resolve Complaint</h2>
              <button onClick={() => { setShowResolveModal(false); setSelectedComplaint(null); setResolution(''); }}><X className="h-5 w-5" /></button>
            </div>
            <p className="text-sm text-gray-600 mb-4">Complaint: {selectedComplaint.subject}</p>
            <div>
              <Label>Resolution Details *</Label>
              <textarea 
                className="w-full p-2 border rounded-lg" 
                rows="4"
                value={resolution}
                onChange={(e) => setResolution(e.target.value)}
                placeholder="Enter resolution details..."
              />
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button type="button" variant="outline" onClick={() => { setShowResolveModal(false); setSelectedComplaint(null); setResolution(''); }}>Cancel</Button>
              <Button onClick={handleResolve}>Mark as Resolved</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplinList;
